import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Check } from "lucide-react";
import { Profile } from "@/integrations/supabase/types/subscription";
import { useToast } from "@/hooks/use-toast";

interface TokenTopUpCardProps {
  profile: Profile;
  loading: boolean;
}

export function TokenTopUpCard({ profile, loading }: TokenTopUpCardProps) {
  const { toast } = useToast();
  
  const { data: subscriptionPlan } = useQuery({
    queryKey: ["subscriptionPlan", profile.subscription_plan_id],
    queryFn: async () => {
      if (!profile.subscription_plan_id) return null;
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("id", profile.subscription_plan_id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!profile.subscription_plan_id
  });

  const { data: tokenPackages, isLoading: packageLoading } = useQuery({
    queryKey: ["tokenPackages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("token_packages")
        .select("*")
        .in('price_id', ['price_1QeOPTL8jiTMrRAKM12H4g2b', 'price_1QeOPTL8jiTMrRAKKKAHV7kx'])
        .eq('active', true)
        .order('price_amount', { ascending: true });
      
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('No active token packages found');
      }
      return data;
    },
  });

  const handleTokenPurchase = async (packageId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Ingen bruker funnet");

      const { data, error } = await supabase.functions.invoke("create-token-purchase", {
        body: { 
          userId: user.id,
          packageId: packageId
        }
      });

      if (error) throw error;
      if (!data?.url) throw new Error("Ingen checkout URL mottatt fra server");

      window.location.href = data.url;
    } catch (error) {
      console.error('Token purchase error:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke starte token kjøp",
        variant: "destructive",
      });
    }
  };

  const isProUser = subscriptionPlan?.name === 'Pro Bruker';
  const discountPercentage = isProUser ? 20 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Token Oppfylling</CardTitle>
        <CardDescription>
          Kjøp ekstra tokens når du trenger det for å fortsette å bruke tjenestene. Tokens utløper aldri og kan brukes til alle tjenester.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {packageLoading ? (
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        ) : tokenPackages && (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              {tokenPackages.map((pkg) => {
                const basePrice = pkg.price_amount / 100;
                const finalPrice = basePrice * (1 - discountPercentage / 100);
                
                return (
                  <div key={pkg.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <p className="text-2xl font-bold">
                          {finalPrice} kr
                          {isProUser && (
                            <span className="ml-2 text-sm text-green-600">
                              (20% rabatt)
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {pkg.token_amount.toLocaleString()} tokens
                        </p>
                      </div>
                      <Button
                        onClick={() => handleTokenPurchase(pkg.id)}
                        disabled={loading}
                        className="bg-amber-500 hover:bg-amber-600"
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Kjøp tokens"
                        )}
                      </Button>
                    </div>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-amber-500" />
                        <span className="text-sm">Umiddelbar bruk</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-amber-500" />
                        <span className="text-sm">Ingen utløpsdato</span>
                      </li>
                      {isProUser && (
                        <li className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-amber-500" />
                          <span className="text-sm">20% Pro bruker rabatt</span>
                        </li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}